import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout

  const poll = useCallback(
    async (attempt = 0) => {
      if (attempt >= 6) {
        setState("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    },
    [sessionId, refresh]
  );

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    poll();
  }, [sessionId, poll]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center border border-border bg-white p-10" data-testid="checkout-return">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto" />
            <h1 className="font-heading font-bold text-2xl mt-6">Confirming your payment…</h1>
            <p className="text-muted-foreground mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-primary mx-auto" />
            <h1 className="font-heading font-black text-3xl mt-6">You're all set!</h1>
            <p className="text-muted-foreground mt-2">Your purchase is confirmed and your documents are ready.</p>
            <Link to="/portal" data-testid="checkout-go-portal"
              className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3.5 font-semibold mt-8 btn-lift">
              Go to my portal <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle className="w-14 h-14 text-accent mx-auto" />
            <h1 className="font-heading font-bold text-2xl mt-6">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="text-muted-foreground mt-2">
              {state === "timeout"
                ? "Your payment is taking longer than expected. Check your portal shortly."
                : "Your payment was not completed. You can try again anytime."}
            </p>
            <Link to="/products" className="inline-flex items-center gap-2 border border-border px-6 py-3.5 font-semibold mt-8 hover:border-primary">
              Back to products
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
